import React, { Suspense, useEffect, useState } from 'react';
import styled from 'styled-components';
// import { Link } from 'react-scroll';
import { FiMenu, FiX } from 'react-icons/fi';

import { IdanLevianLogo } from '../assets/logo/idanlevian';

const ResumeDropdown = React.lazy(() => import('./ResumeDropdown'));

// Navbar Container
const NavbarWrapper = styled.nav.attrs({ role: 'navigation' })`
  position: fixed;
  top: 0;
  width: 100vw;
  background: ${({ theme }) => theme.colors.background.dark + 'f0'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  box-shadow: 0 0 15px ${({ theme }) => theme.colors.primary.aqua};
  z-index: 1000;

  @media (max-width: 980px) {
    padding: 1rem;
    //box-shadow: none
  }
`;

// Logo Container
const Logo = styled.div`
  cursor: pointer;
`;

// Centered NavLinks
const NavLinks = styled.ul`
  display: flex;
  justify-content: center;
  flex-grow: 1;
  gap: 2rem;
  list-style: none;
  margin: 0;

  @media (max-width: 980px) {
    display: none;
  }
`;

// Each Nav Item
const NavLink = styled.li<{ $isActive: boolean }>`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  position: relative;
  cursor: pointer;
  transition: color 0.3s ease;
  list-style: none;

  &:hover {
    color: ${({ theme }) => theme.colors.primary.aqua};
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: ${({ $isActive }) => ($isActive ? '100%' : '0')};
    height: 2px;
    background: ${({ theme }) => theme.colors.primary.blue};
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }
`;

// Mobile Menu Button
const MobileMenuButton = styled.div`
  display: none;
  cursor: pointer;

  @media (max-width: 980px) {
    display: block;
    font-size: 1.5rem;
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

// Mobile Navigation Links
const MobileNavLinks = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${({ theme }) => theme.colors.background.dark};
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  width: 100%;
  z-index: 999;

  a {
    cursor: pointer;
    font-size: 1.25rem;
    color: ${({ theme }) => theme.colors.text.primary};
    text-align: center;

    &:hover {
      color: ${({ theme }) => theme.colors.primary.blue};
    }
  }
`;

// Wrapper for Right-Side Content
const RightSectionWrapper = styled.div`
  @media (max-width: 980px) {
    display: none;
  }
`;

const MenuIcon = FiMenu as unknown as React.ComponentType;
const CloseIcon = FiX as unknown as React.ComponentType;

const Navbar: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'experience', 'portfolio', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (
          element &&
          element.offsetTop <= scrollPosition &&
          element.offsetTop + element.offsetHeight > scrollPosition
        ) {
          setActiveSection(section);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, section: string) => {
    e.preventDefault();
    const el = document.getElementById(section);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(section);
    }
  };

  return (
    <NavbarWrapper>
      {/* Logo */}
      <Logo>
        <a href="#home" onClick={(e) => handleNavClick(e, 'home')} aria-label="Home">
          <IdanLevianLogo />
        </a>
      </Logo>

      {/* Centered Navigation Links */}
      <NavLinks>
        {['home', 'about', 'experience', 'portfolio'].map((section) => (
          <NavLink key={section} $isActive={activeSection === section}>
            <a
              href={`#${section}`}
              onClick={(e) => handleNavClick(e, section)}
              aria-label={section.charAt(0).toUpperCase() + section.slice(1)}
            >
              {section.charAt(0).toUpperCase() + section.slice(1)}
            </a>
          </NavLink>
        ))}
      </NavLinks>

      {/* Right-Aligned Content */}
      <RightSectionWrapper>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <NavLink $isActive={activeSection === 'contact'}>
            <a
              href="#contact"
              onClick={(e) => handleNavClick(e, 'contact')}
              aria-label="Contact Me"
            >
              Contact Me
            </a>
          </NavLink>
          <Suspense fallback={null}>
            <ResumeDropdown />
          </Suspense>
        </div>
      </RightSectionWrapper>

      {/* Mobile Menu Button */}
      <MobileMenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
        {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
      </MobileMenuButton>

      {/* Mobile Navigation */}
      <MobileNavLinks $isOpen={isMenuOpen}>
        {['home', 'about', 'experience', 'portfolio', 'contact'].map((section) => (
          <a
            key={section}
            href={`#${section}`}
            onClick={(e) => handleNavClick(e, section)}
            aria-label={section.charAt(0).toUpperCase() + section.slice(1)}
          >
            {section.charAt(0).toUpperCase() + section.slice(1)}
          </a>
        ))}
        <Suspense fallback={null}>
          <ResumeDropdown />
        </Suspense>
      </MobileNavLinks>
    </NavbarWrapper>
  );
};

export default Navbar;
